import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { Toaster } from '@kir/ui/components/sonner'
import { Spinner } from '@kir/ui/components/spinner'
import { AppShell } from '@/components/layout/app-shell'
import { AccountMenu } from '@/features/auth/account-menu'
import { IdleWarning } from '@/features/auth/idle-warning'
import { NoDealership } from '@/features/auth/no-dealership'
import { authQueries } from '@/features/auth/queries'
import {
  hasSession,
  lastSignOutReason,
  resolveTenantId,
  signOut,
  useAccessToken,
} from '@/features/auth/session'
import { useSession } from '@/features/auth/use-session'
import { blockedBy } from '@/features/security/enforcement'
import { EnrolmentRequired } from '@/features/security/enrolment-required'
import { ApiError, tenantStore } from '@/lib/api/client'
import { useResolvedTheme } from '@/lib/theme'

/**
 * The prototype's toast sits 16px from each side and 80px from the bottom, clear of the 64px tab
 * bar. `--toast-bottom` (in `index.css`) is that on a phone and a plain margin from `md`, where
 * there is no tab bar to clear; a CSS variable because `sonner` switches from `offset` to
 * `mobileOffset` at 600px, not at `md`, so neither prop alone can follow the layout.
 */
const TOAST_OFFSET = {
  bottom: 'var(--toast-bottom)',
  left: '1rem',
  right: '1rem',
}

/** Where to send the user back to after signing in. The home page needs no parameter. */
function returnTo(href: string): string | undefined {
  return href === '/' ? undefined : href
}

/**
 * Pathless layout for everything that needs a signed-in user. `beforeLoad` runs before any
 * child loader: no token means straight to sign-in, a rejected token means the same with a
 * note, otherwise the profile is in the cache and a dealership is selected before render.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    // Not `hasValidToken`: a stale access token is the normal state of a tab that has been open
    // fifteen minutes, and the session cookie behind it usually has days left. Signing the user
    // out without asking the API is the bug this guard used to have.
    if (!(await hasSession())) {
      throw redirect({ to: '/login', search: { redirect: returnTo(location.href) } })
    }
    try {
      const me = await context.queryClient.ensureQueryData(authQueries.me())
      const tenantId = resolveTenantId(me.memberships, tenantStore.get())
      if (tenantId !== tenantStore.get()) tenantStore.set(tenantId)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        signOut(context.queryClient, 'expired')
        throw redirect({
          to: '/login',
          search: { redirect: returnTo(location.href), reason: 'expired' },
        })
      }
      throw error
    }
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  ),
  component: AppLayout,
})

function AppLayout() {
  const token = useAccessToken()
  // The token can vanish while this layout is mounted (sign out, 401 mid-session, another tab).
  return token === null ? <SignedOut /> : <SignedInApp />
}

/**
 * The way out, taken once.
 *
 * A member who chose to leave is where they meant to be; one who was put here — by an expiry or
 * by the dealership's idle timeout — is told why, and offered the screen they were on. That
 * screen is read the moment the token goes and never again: this layout stays mounted until the
 * sign-in route has loaded, and is re-rendered by the very navigation it asked for. Reading the
 * address then would find `/login?redirect=…` and put it inside itself — which is what
 * `<Navigate>` did here, navigating again on every render with a redirect nested one level
 * deeper each time, until the tab ran out of memory.
 */
function SignedOut() {
  const href = useLocation({ select: (location) => location.href })
  const navigate = useNavigate()
  const [target] = useState(() => {
    const reason = lastSignOutReason()
    const involuntary = reason !== 'user'
    return {
      redirect: involuntary ? returnTo(href) : undefined,
      reason: involuntary ? reason : undefined,
    }
  })

  useEffect(() => {
    void navigate({ to: '/login', replace: true, search: target })
  }, [navigate, target])

  return null
}

function SignedInApp() {
  const { user, memberships, membership } = useSession()
  // The dealership requires two-step verification and this member has not set it up, so the API
  // refuses every request they could make from in here (ADR 0009). The blocking screen replaces
  // the app rather than sitting over it: the route underneath is untouched, so enrolling drops
  // the member back where they were going with nothing having had to remember it.
  const blocking = blockedBy(user, membership)
  return (
    <>
      {memberships.length === 0 ? (
        <NoDealership />
      ) : blocking !== null ? (
        <EnrolmentRequired dealership={blocking.name} />
      ) : (
        <AppShell
          dealership={<AccountMenu variant="pill" />}
          account={<AccountMenu variant="compact" />}
        >
          <Outlet />
        </AppShell>
      )}
      <IdleWarning />
      <ToastHost />
    </>
  )
}

/**
 * One toast host for the whole signed-in app. `sonner` has no Next.js theme provider here, so the
 * resolved theme comes in as a prop. The same offset goes to both of `sonner`'s props so the
 * variable inside it decides, whichever one `sonner` picks at a given width.
 */
function ToastHost() {
  const theme = useResolvedTheme()
  return (
    <Toaster
      theme={theme}
      position="bottom-center"
      offset={TOAST_OFFSET}
      mobileOffset={TOAST_OFFSET}
    />
  )
}
